import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Gift } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../../api/apiServices";

export default function VerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [promoReward, setPromoReward] = useState(null);

  const inputRefs = useRef([]);

  const email = location.state?.email || "";
  const phone = location.state?.phone || "";
  // Which channel the OTP was actually sent on (set by Signup from the
  // register response — admin-controlled via Platform Settings). Login's
  // "unverified account" redirect never sets this, so it defaults to
  // EMAIL there, which matches that flow always using an email address.
  // This has to match the real channel: verify-otp/otp-resend key off
  // whichever field is present, and Twilio-checking a phone that was
  // never sent a Twilio code (or vice versa) always fails.
  const otpChannel = location.state?.otpChannel || "EMAIL";
  const useSms = otpChannel === "SMS" && Boolean(phone);
  const destination = useSms ? phone : email;
  // Passed from Signup so we know whether the user actually entered a code —
  // used only to decide whether it's worth waiting to show a reward screen,
  // never to claim the code worked before the backend confirms it.
  const enteredPromoCode = location.state?.promoCode || null;

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError("");

    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      setCode(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const isValid = code.every((digit) => digit !== "");

  const goToDashboard = () => navigate("/dashboard");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setIsSubmitting(true);
    setError("");

    try {
      const payload = useSms ? { phone, otp: code.join("") } : { email, otp: code.join("") };

      console.log("Verifying OTP with payload:", payload);
      const response = await apiClient.post("/auth/verify-otp", payload);
      console.log("OTP response:", response.data);

      if (response.data?.token) {
        localStorage.setItem("user_token", response.data.token);

        // Only show the reward screen if the backend actually confirms a
        // credit — a code that didn't match anything is dropped silently
        // (see promo.service.js), so no reward here just means "no reward,"
        // never "your code was wrong."
        if (response.data?.promoReward?.amount) {
          setPromoReward(response.data.promoReward);
          setTimeout(goToDashboard, 2200);
          return;
        }

        goToDashboard();
        return;
      }

      navigate("/login");
    } catch (err) {
      console.error("OTP verification failed:", err);
      let errMsg = "Invalid or expired code. Please try again.";
      if (err.response?.data?.message) errMsg = err.response.data.message;
      else if (err.response?.status === 400 || err.response?.status === 401)
        errMsg = "Incorrect or expired verification code.";
      setError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    try {
      console.log("Resending OTP to:", destination);
      await apiClient.post("/auth/otp/resend", useSms ? { phone } : { email });

      setResendTimer(60);
      setCanResend(false);
      setError("");
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Failed to resend code. Please try again.");
    }
  };

  const formatTimer = () => {
    const minutes = Math.floor(resendTimer / 60);
    const seconds = resendTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (promoReward) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-[#F5F6FA] flex flex-col items-center justify-center px-6 text-center"
      >
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6"
        >
          <Gift size={32} className="text-emerald-600" />
        </motion.div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're verified!</h1>
        <p className="text-base text-gray-600">
          Your welcome bonus of{" "}
          <span className="font-semibold text-gray-900">
            ₦{Number(promoReward.amount).toLocaleString()}
          </span>{" "}
          has been credited to your wallet.
        </p>
        <p className="text-sm text-gray-400 mt-6">Taking you to your dashboard...</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="min-h-screen bg-[#F5F6FA] flex flex-col px-5 sm:px-6 pt-6 pb-10"
    >
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-8">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </motion.button>
        <motion.span
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/login")}
          className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer transition"
        >
          Log in
        </motion.span>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {useSms ? "Check your phone" : "Check your email"}
      </h1>
      <p className="text-base text-gray-600 mb-10">
        We sent a verification code to <span className="font-medium text-gray-800">{destination}. </span>
        {useSms
          ? "It should arrive by SMS within a minute."
          : "If you can not see the code please check your spam box"}
      </p>

      <form onSubmit={handleSubmit} className="space-y-8 flex-1">
        <div className="flex justify-center gap-3 sm:gap-4">
          {code.map((digit, index) => (
            <motion.input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold rounded-xl border-2 transition-all duration-200 outline-none bg-white shadow-sm focus:shadow-md
                ${error ? "border-red-500 focus:ring-red-200" : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"}`}
              whileFocus={{ scale: 1.08 }}
            />
          ))}
        </div>

        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-sm text-red-600 text-center font-medium"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <motion.button
          type="submit"
          disabled={!isValid || isSubmitting}
          whileTap={{ scale: 0.97 }}
          className={`w-full py-4 rounded-full font-semibold text-base shadow-md transition-all duration-300
            ${isValid && !isSubmitting ? "bg-[#344054] text-white hover:bg-[#2a3745]" : "bg-[#D0D5DD] text-gray-500 cursor-not-allowed"}`}
        >
          {isSubmitting ? "Verifying..." : "Submit"}
        </motion.button>

        <div className="text-center mt-6">
          {canResend ? (
            <button
              type="button"
              onClick={handleResend}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition"
            >
              Didn't receive code? Send again
            </button>
          ) : (
            <p className="text-sm text-gray-500">Code will expire in {formatTimer()}</p>
          )}
        </div>
      </form>

      <div className="text-center pb-8 text-sm text-gray-500 font-medium mt-6">DataBling</div>
    </motion.div>
  );
}